import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useContacts } from '../../contacts/api/useContacts';
import { useCurrencies } from '../../currencies/api/useCurrencies';
import { ErrorMessage } from '../../../shared/ui/ErrorMessage';
import { Loading } from '../../../shared/ui/Loading';
import { PageHeader } from '../../../shared/ui/PageHeader';
import { EmptyState } from '../../../shared/ui/EmptyState';
import { FilterSheet } from '../../../shared/ui/FilterSheet';
import { usePurchases } from '../api/useTrades';

const LIMIT = 50;

const EMPTY = {
  status: '',
  paymentStatus: '',
  currencyId: '',
  contactId: '',
  dateFrom: '',
  dateTo: '',
};

export function PurchasesListPage() {
  const { t, i18n } = useTranslation();
  const [f, setF] = useState(EMPTY);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [filterOpen, setFilterOpen] = useState(false);

  const currencies = useCurrencies();
  const contacts = useContacts();

  function setField(key: string, value: string) {
    setPage(0);
    setF((prev) => ({ ...prev, [key]: value }));
  }

  const query = {
    ...(f.status ? { status: f.status } : {}),
    ...(f.paymentStatus ? { paymentStatus: f.paymentStatus } : {}),
    ...(f.currencyId ? { currencyId: f.currencyId } : {}),
    ...(f.contactId ? { contactId: f.contactId } : {}),
    ...(f.dateFrom ? { dateFrom: f.dateFrom } : {}),
    ...(f.dateTo ? { dateTo: f.dateTo } : {}),
    limit: LIMIT,
    offset: page * LIMIT,
  };

  const q = usePurchases(query);
  const activeFilterCount = Object.values(f).filter(Boolean).length;
  const totalPages = q.data ? Math.ceil(q.data.total / LIMIT) : 1;

  const dateFmt = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' });
  const dayFmt = new Intl.DateTimeFormat(i18n.language, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  // Group items by calendar date
  type PurchaseItem = NonNullable<typeof q.data>['data'][number];
  const grouped: Map<string, PurchaseItem[]> = new Map();
  if (q.data) {
    for (const p of q.data.data) {
      const day = p.transactionDate.slice(0, 10);
      if (!grouped.has(day)) grouped.set(day, []);
      const bucket = grouped.get(day);
      if (bucket) bucket.push(p);
    }
  }

  // Client-side search filter on top of server pagination
  const filteredGrouped: Map<string, PurchaseItem[]> = new Map();
  if (search.trim()) {
    const needle = search.trim().toLowerCase();
    for (const [day, rows] of grouped) {
      const filtered = rows.filter((p) => {
        const code = currencies.data?.find((c) => c.id === p.deliveredCurrencyId)?.code ?? '';
        return (
          p.deliveredAmount.includes(needle) ||
          p.paymentTotal.includes(needle) ||
          code.toLowerCase().includes(needle) ||
          (p.reference ?? '').toLowerCase().includes(needle)
        );
      });
      if (filtered.length > 0) filteredGrouped.set(day, filtered);
    }
  } else {
    for (const [day, rows] of grouped) filteredGrouped.set(day, rows);
  }

  return (
    <>
      <PageHeader
        title={t('purchases.title')}
        action={
          <Link to="/purchases/new" className="btn btn--primary">
            {t('purchases.new')}
          </Link>
        }
      />

      <div className="search-row">
        <input
          className="search-row__input"
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('common.search')}
          aria-label={t('common.search')}
        />
        <button
          type="button"
          className={`filter-pill${activeFilterCount > 0 ? ' has-filters' : ''}`}
          aria-expanded={filterOpen}
          onClick={() => setFilterOpen(true)}
        >
          {t('common.filters')}
          {activeFilterCount > 0 ? (
            <span className="filter-pill__count">{activeFilterCount}</span>
          ) : null}
        </button>
      </div>

      {filterOpen ? (
        <FilterSheet onClose={() => setFilterOpen(false)}>
          <div className="field">
            <label>{t('purchases.status_label')}</label>
            <select value={f.status} onChange={(e) => setField('status', e.target.value)}>
              <option value="">{t('common.filter.all_statuses')}</option>
              <option value="CONFIRMED">{t('purchases.status.CONFIRMED')}</option>
              <option value="CANCELLED">{t('purchases.status.CANCELLED')}</option>
              <option value="REVERSED">{t('purchases.status.REVERSED')}</option>
            </select>
          </div>
          <div className="field">
            <label>{t('purchases.payment_status_label')}</label>
            <select
              value={f.paymentStatus}
              onChange={(e) => setField('paymentStatus', e.target.value)}
            >
              <option value="">{t('common.filter.all_payment_statuses')}</option>
              <option value="UNPAID">{t('purchases.payment_status.UNPAID')}</option>
              <option value="PARTIALLY_PAID">{t('purchases.payment_status.PARTIALLY_PAID')}</option>
              <option value="PAID">{t('purchases.payment_status.PAID')}</option>
            </select>
          </div>
          <div className="field">
            <label>{t('purchases.from_currency')}</label>
            <select value={f.currencyId} onChange={(e) => setField('currencyId', e.target.value)}>
              <option value="">{t('common.filter.all_currencies')}</option>
              {currencies.data?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.code} — {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>{t('purchases.contact_optional')}</label>
            <select value={f.contactId} onChange={(e) => setField('contactId', e.target.value)}>
              <option value="">{t('common.filter.all_contacts')}</option>
              {contacts.data?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>{t('common.filter.date_from')}</label>
            <input
              type="date"
              value={f.dateFrom}
              onChange={(e) => setField('dateFrom', e.target.value)}
            />
          </div>
          <div className="field">
            <label>{t('common.filter.date_to')}</label>
            <input
              type="date"
              value={f.dateTo}
              onChange={(e) => setField('dateTo', e.target.value)}
            />
          </div>
          {activeFilterCount > 0 ? (
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => {
                setF(EMPTY);
                setPage(0);
              }}
            >
              {t('common.reset_filters')}
            </button>
          ) : null}
        </FilterSheet>
      ) : null}

      {q.isLoading ? <Loading /> : null}
      {q.error ? <ErrorMessage error={q.error} /> : null}

      {q.data && filteredGrouped.size === 0 ? (
        <EmptyState
          icon="🧾"
          title={t('purchases.empty')}
          primaryAction={
            <Link to="/purchases/new" className="btn btn--primary">
              {t('purchases.new')}
            </Link>
          }
        />
      ) : null}

      {filteredGrouped.size > 0
        ? Array.from(filteredGrouped.entries()).map(([day, rows]) => {
            const dayTotal = rows
              .reduce((sum, p) => sum + parseFloat(p.paymentTotal), 0)
              .toFixed(2);
            const dayDate = new Date(day + 'T00:00:00');
            return (
              <div key={day} className="day-group">
                <div className="day-group__header">
                  <span className="day-group__label">{dayFmt.format(dayDate)}</span>
                  <span className="day-group__total">{dayTotal} MRU</span>
                </div>
                <ul className="card-list">
                  {rows.map((p) => {
                    const code =
                      currencies.data?.find((c) => c.id === p.deliveredCurrencyId)?.code ?? '???';
                    return (
                      <li key={p.id}>
                        <Link to={`/purchases/${p.id}`} className="card-row">
                          <div className="card-row__header">
                            <h2 className="card-row__title">
                              {p.deliveredAmount} {code}
                            </h2>
                            <span className={`badge badge--${paymentStatusClass(p.paymentStatus)}`}>
                              {t(`purchases.payment_status.${p.paymentStatus}`)}
                            </span>
                          </div>
                          <div className="card-row__meta">
                            <span className="card-row__mono">
                              {dateFmt.format(new Date(p.transactionDate))}
                            </span>
                            <span>
                              {p.paymentTotal}{' '}
                              {currencies.data?.find((c) => c.id === p.paymentCurrencyId)?.code ??
                                ''}
                            </span>
                          </div>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })
        : null}

      {q.data && q.data.total > LIMIT ? (
        <div className="pagination">
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => setPage((p) => p - 1)}
            disabled={page === 0}
          >
            {t('common.prev_page')}
          </button>
          <span className="pagination__info">
            {t('common.page_of', { n: page + 1, total: totalPages })}
          </span>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => setPage((p) => p + 1)}
            disabled={page >= totalPages - 1}
          >
            {t('common.next_page')}
          </button>
        </div>
      ) : null}
    </>
  );
}

function paymentStatusClass(status: string) {
  if (status === 'PAID') return 'in';
  if (status === 'PARTIALLY_PAID') return 'warn';
  return 'danger';
}
